import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Wrench, Spinner, ArrowsClockwise, UserCircle,
  Trophy, CheckCircle, Warning, CurrencyDollar, ChartBar,
} from "@phosphor-icons/react";
import { fetchServiceTechLeaderboard } from "@/lib/api";
import AccountMenu from "@/components/AccountMenu";
import { useT } from "@/lib/i18n";

const RANGES = [
  { v: 7, l: "7 d" },
  { v: 30, l: "30 d" },
  { v: 90, l: "90 d" },
  { v: 365, l: "1 yr" },
];

/**
 * /admin/service-techs — staff-only leaderboard. Per-tech rollup of
 * visit counts, AI confidence, SN-match rate, and revenue allocated.
 */
export default function ServiceTechLeaderboardPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [days, setDays] = useState(30);
  const [sortBy, setSortBy] = useState("visits");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await fetchServiceTechLeaderboard(days);
      setData(r);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load leaderboard");
    } finally {
      setLoading(false);
    }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const items = (data?.items || []).slice().sort((a, b) => {
    const dir = -1;  // desc
    const av = a[sortBy], bv = b[sortBy];
    if (av == null && bv == null) return 0;
    if (av == null) return -dir;
    if (bv == null) return dir;
    return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="svc-tech-leaderboard-page">
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-14 flex items-center gap-3">
          <button onClick={() => navigate("/")} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500" data-testid="lb-back">
            <ArrowLeft size={18} weight="bold" />
          </button>
          <Trophy size={20} weight="fill" className="text-amber-500" />
          <h1 className="text-base md:text-lg font-black tracking-tighter font-['Chivo']">{t("svc_tech_lb.title", "Service-Tech Leaderboard")}</h1>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded-sm">Admin</span>
          <button onClick={load} disabled={loading} className="ml-auto inline-flex items-center gap-1.5 text-xs px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-100 disabled:opacity-50" data-testid="lb-refresh">
            {loading ? <Spinner size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />}
            {t("action.refresh", "Refresh")}
          </button>
          <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-8">
        <div className="flex items-center gap-1 mb-4" data-testid="lb-range-bar">
          {RANGES.map((r) => (
            <button
              key={r.v}
              onClick={() => setDays(r.v)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm border ${days === r.v ? "bg-violet-600 text-white border-violet-700" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
              data-testid={`lb-range-${r.v}`}
            >
              {r.l}
            </button>
          ))}
        </div>

        {error && <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-sm px-3 py-2 mb-3" data-testid="lb-error">{String(error)}</div>}

        {loading && !items.length ? (
          <div className="bg-white rounded-md border border-slate-200 p-8 text-center"><Spinner size={20} className="animate-spin text-slate-400 mx-auto mb-1" /><div className="text-xs text-slate-500">Loading…</div></div>
        ) : !items.length ? (
          <div className="bg-white rounded-md border border-slate-200 p-12 text-center text-sm text-slate-500" data-testid="lb-empty">{t("svc_tech_lb.empty", "No visits in the selected range.")}</div>
        ) : (
          <div className="bg-white rounded-md border border-slate-200 overflow-hidden" data-testid="lb-table">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <Th>#</Th>
                  <Th>Tech</Th>
                  <SortHeader label="Visits" col="visits" sortBy={sortBy} setSortBy={setSortBy} />
                  <Th className="text-center">Status</Th>
                  <SortHeader label={t("svc_tech_lb.col_sn_match", "SN match%")} col="sn_match_pct" sortBy={sortBy} setSortBy={setSortBy} className="text-right" />
                  <SortHeader label={t("svc_tech_lb.col_confidence", "Confidence")} col="avg_confidence" sortBy={sortBy} setSortBy={setSortBy} className="text-right" />
                  <SortHeader label={t("svc_tech_lb.col_splits", "Splits")} col="splits" sortBy={sortBy} setSortBy={setSortBy} className="text-right" />
                  <SortHeader label={t("svc_tech_lb.col_revenue", "$ Allocated")} col="revenue_allocated" sortBy={sortBy} setSortBy={setSortBy} className="text-right" />
                  <Th className="text-right">{t("svc_tech_lb.col_last_seen", "Last seen")}</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, i) => (
                  <tr key={r.tech_email} className="border-b border-slate-100 hover:bg-blue-50/40" data-testid={`lb-row-${r.tech_email}`}>
                    <td className="px-3 py-2 text-slate-400 font-bold w-8">
                      {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <UserCircle size={20} weight="duotone" className="text-slate-400" />
                        <span className="font-bold text-slate-800 truncate">{r.tech_email}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 font-bold text-slate-800">{r.visits}</td>
                    <td className="px-3 py-2 text-center text-[10px] uppercase tracking-wider">
                      <span className="text-emerald-700">{r.completed} ✓</span>
                      {r.draft > 0 && <span className="ml-1 text-amber-700">{r.draft} ◔</span>}
                      {r.blocked > 0 && <span className="ml-1 text-rose-700">{r.blocked} ⊘</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.sn_match_pct == null ? <span className="text-slate-300">—</span> : (
                        <span className={`text-[11px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${r.sn_match_pct >= 80 ? "bg-emerald-100 text-emerald-700" : r.sn_match_pct >= 50 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>
                          {r.sn_match_pct}%
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-700">
                      {r.avg_confidence == null ? <span className="text-slate-300">—</span> : `${Math.round(r.avg_confidence * 100)}%`}
                    </td>
                    <td className="px-3 py-2 text-right text-violet-700">{r.splits || 0}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-800">${(r.revenue_allocated || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-slate-500 text-[11px]">{r.last_seen_at ? relTime(r.last_seen_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data && (
          <div className="mt-4 text-[11px] text-slate-500 flex items-center gap-3">
            <ChartBar size={12} weight="fill" />
            <span>Window: <strong>{data.days} days</strong> · Techs: <strong>{data.count}</strong></span>
          </div>
        )}
      </main>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 text-left ${className}`}>{children}</th>;
}

function SortHeader({ label, col, sortBy, setSortBy, className = "" }) {
  const active = sortBy === col;
  return (
    <th className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-left ${className}`}>
      <button onClick={() => setSortBy(col)} className={`hover:text-blue-700 ${active ? "text-blue-700" : "text-slate-500"}`} data-testid={`lb-sort-${col}`}>
        {label}{active ? " ↓" : ""}
      </button>
    </th>
  );
}

function relTime(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const diff = (Date.now() - t) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return "—"; }
}
